import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { colors, typography } from '../styles/theme';
import { isValidEmail } from '../Access/styles/authPanel.styles';
import { useAuth } from '../Context/AuthContext';
import { FORM_PRIMARY_BUTTON_SX } from '../Map/components/DockPanel/Tabs/formBlockUi';
import { toast } from './Toast';
import InfoNotice from './InfoNotice';
import {
  INQUIRY_EMAIL_FIELD_CODES,
  LOT_INQUIRY_NOT_ENABLED_MESSAGE,
  sendCollectionInquiry,
} from '../services/collectionInquiryService';
import { isValidCollectionInquiryName } from '../Map/utils/collectionInquiryForm';
import { savePostLoginRedirect } from '../Utils/postLoginRedirect';
import {
  showroomBrandedFieldSx,
  showroomBrandedPrimaryButtonSx,
} from '../Showroom/Showroom.styles';

export const COLLECTION_INQUIRY_FORM_ID = 'collection-inquiry-form';

const MAX_NAME = 120;
const MAX_PHONE = 40;
const MAX_MESSAGE = 2000;
const PHONE_CHARS_REGEX = /^[\d\s+\-().]*$/;
const PHONE_MIN_DIGITS = 8;
const PHONE_MAX_DIGITS = 15;

function defaultEmailFromUser(user) {
  if (!user || typeof user !== 'object') return '';
  return String(user.email || '').trim();
}

function defaultNameFromUser(user) {
  if (!user || typeof user !== 'object') return '';
  return String(user.name || user.given_name || '').trim().slice(0, MAX_NAME);
}

function sanitizePhoneInput(raw) {
  return String(raw || '')
    .replace(/[^\d\s+\-().]/g, '')
    .slice(0, MAX_PHONE);
}

/** Vacío = ok (opcional). Si hay valor, solo chars de teléfono y 8–15 dígitos. */
function isValidOptionalPhone(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return true;
  if (!PHONE_CHARS_REGEX.test(trimmed)) return false;
  const digits = trimmed.replace(/\D/g, '');
  return digits.length >= PHONE_MIN_DIGITS && digits.length <= PHONE_MAX_DIGITS;
}

function buildInquiryBody({
  kind,
  projectId,
  parcelId,
  shareToken,
  listId,
  lotId,
  userLotId,
  name,
  email,
  phone,
  message,
}) {
  const contact = {
    name,
    email,
    message,
    ...(phone ? { phone } : {}),
  };
  if (kind === 'lot') {
    // F3: lote / UF compartido solo. Sin `user_lot_id` la consulta es por la parcela.
    return {
      kind: 'lot',
      share_token: shareToken,
      ...(userLotId ? { user_lot_id: userLotId } : {}),
      ...contact,
    };
  }
  if (kind === 'list') {
    return {
      kind: 'list',
      ...(shareToken ? { share_token: shareToken } : { list_id: listId }),
      ...(userLotId ? { user_lot_id: userLotId } : (lotId ? { lot_id: lotId } : {})),
      ...contact,
    };
  }
  return {
    kind: 'showroom',
    project_id: projectId,
    parcel_id: parcelId,
    ...contact,
  };
}

/**
 * Consulta de colección → POST /sendcollectioninquiry.
 * Ver la lista pública puede ser anónimo; enviar pide JWT (lista y CAD).
 * Sin sesión el botón va a login y vuelve al share.
 *
 * CAD: `kind: 'showroom'` + `project_id` + `parcel_id`.
 * Lista: `kind: 'list'` + `share_token` o `list_id` + `user_lot_id` o `lot_id` (no ambos).
 * Lote compartido (F3): `kind: 'lot'` + `share_token` + `user_lot_id` opcional.
 * Si la API todavía no lo acepta (`LOT_INQUIRY_NOT_ENABLED` o API vieja), el
 * form queda reemplazado por un aviso, sin reintento.
 *
 * Email (SQL 098 / 099):
 * - Visitante logueado: el de su cuenta, **solo lectura** (la API usa el del JWT).
 *   Si la cuenta no trae email, se puede escribir. Sin texto de ayuda.
 * - Dueño / editor en su propio share (`manage`): aviso arriba del form
 *   (“Estás cargando una consulta para un tercero.” + “Usá los datos de
 *   contacto del cliente.”); label “Email”, editable y **vacío** (nombre
 *   tampoco se precarga; sin ayuda ni placeholder en los campos). `manage`
 *   sale de `viewer_can_manage` del GET del share (099) o del `can_edit`
 *   in-app. Si igual no lo sabe, el 400 `INQUIRY_OWNER_EMAIL` pasa el form a
 *   ese modo y marca el campo (“No podés usar tu propio email; cargá el del
 *   cliente.”).
 */
export default function CollectionInquiryForm({
  kind = 'showroom',
  projectId = '',
  parcelId = '',
  shareToken = '',
  listId = '',
  lotId = '',
  userLotId = '',
  parcelLabel,
  accentColor = null,
  loginRedirectPath = '',
  formId = COLLECTION_INQUIRY_FORM_ID,
  manage = false,
}) {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [contactPrefilled, setContactPrefilled] = useState(false);
  const [notEnabled, setNotEnabled] = useState(false);
  // `manage` del padre o detectado por el 400 INQUIRY_OWNER_EMAIL.
  const [manageDetected, setManageDetected] = useState(false);
  const [emailError, setEmailError] = useState('');
  const manageMode = manage || manageDetected;
  const accountEmail = defaultEmailFromUser(user);
  const emailLocked = Boolean(isLoggedIn && !manageMode && accountEmail);

  // El aviso de 503 no queda pegado a otra unidad / otro share.
  useEffect(() => {
    setNotEnabled(false);
  }, [kind, shareToken, userLotId]);

  useEffect(() => {
    if (contactPrefilled || !user || manageMode) return;
    const nextEmail = defaultEmailFromUser(user);
    const nextName = defaultNameFromUser(user);
    if (nextEmail) setEmail(nextEmail);
    if (nextName) setName(nextName);
    setContactPrefilled(true);
  }, [user, contactPrefilled, manageMode]);

  // `viewer_can_manage` puede llegar después de precargar la cuenta (GET async):
  // al pasar a modo cliente se vacían nombre y email precargados.
  useEffect(() => {
    if (!manageMode) return;
    setEmail((prev) => (prev && prev === defaultEmailFromUser(user) ? '' : prev));
    setName((prev) => (prev && prev === defaultNameFromUser(user) ? '' : prev));
  }, [manageMode, user]);

  // Con la cuenta, el email es siempre el de la cuenta (no queda uno viejo).
  useEffect(() => {
    if (emailLocked) setEmail(accountEmail);
  }, [emailLocked, accountEmail]);

  const resetForm = () => {
    setName(manageMode ? '' : defaultNameFromUser(user));
    setEmail(manageMode ? '' : defaultEmailFromUser(user));
    setPhone('');
    setMessage('');
    setEmailError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedProjectId = String(projectId || '').trim();
    const trimmedParcelId = String(parcelId || '').trim();
    const trimmedShareToken = String(shareToken || '').trim();
    const trimmedListId = String(listId || '').trim();
    const trimmedLotId = String(lotId || '').trim();
    const trimmedUserLotId = String(userLotId || '').trim();
    const trimmedName = name.trim().slice(0, MAX_NAME);
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPhone = phone.trim().slice(0, MAX_PHONE);
    const trimmedMessage = message.trim().slice(0, MAX_MESSAGE);

    if (!isLoggedIn) {
      if (loginRedirectPath) savePostLoginRedirect(loginRedirectPath);
      navigate('/login');
      return;
    }

    const missingTarget = kind === 'lot'
      ? !trimmedShareToken
      : kind === 'list'
        ? (!trimmedShareToken && !trimmedListId)
        : (!trimmedProjectId || !trimmedParcelId);
    if (missingTarget) {
      toast.error('No se pudo identificar el lote.');
      return;
    }
    if (kind === 'list' && !trimmedUserLotId && !trimmedLotId) {
      toast.error('Abrí una unidad o el lote de la lista para consultar.');
      return;
    }
    if (!isValidCollectionInquiryName(trimmedName)) {
      toast.error('Ingresá tu nombre (no solo números).');
      return;
    }
    if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
      setEmailError(manageMode ? 'Cargá el email del cliente.' : 'Ingresá un email válido.');
      toast.error(manageMode ? 'Cargá el email del cliente.' : 'Ingresá un email válido.');
      return;
    }
    if (!isValidOptionalPhone(trimmedPhone)) {
      toast.error('Ingresá un teléfono válido (solo números, +, espacios o guiones).');
      return;
    }
    if (!trimmedMessage) {
      toast.error('Escribí un mensaje para consultar.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await sendCollectionInquiry(buildInquiryBody({
        kind,
        projectId: trimmedProjectId,
        parcelId: trimmedParcelId,
        shareToken: trimmedShareToken,
        listId: trimmedListId,
        lotId: trimmedLotId,
        userLotId: trimmedUserLotId,
        name: trimmedName,
        email: trimmedEmail,
        phone: trimmedPhone,
        message: trimmedMessage,
      }));
      if (result._httpStatus === 401) {
        if (loginRedirectPath) savePostLoginRedirect(loginRedirectPath);
        navigate('/login');
        return;
      }
      if (result.registered) {
        toast.warning(result.message || 'Quedó registrada; el mail no salió.');
        resetForm();
        return;
      }
      if (result.notEnabled) {
        toast.info(LOT_INQUIRY_NOT_ENABLED_MESSAGE);
        setNotEnabled(true);
        return;
      }
      if (result.rateLimited || result._httpStatus === 429) {
        toast.error(result.message || 'Ya mandaste varias consultas en esta lista. Probá más tarde.');
        return;
      }
      if (!result.ok) {
        if (result.code === 'INQUIRY_OWNER_EMAIL') {
          // Dueño / editor: el email es el del cliente, no el suyo.
          setManageDetected(true);
          setEmail('');
        }
        if (INQUIRY_EMAIL_FIELD_CODES.includes(result.code) || result.field === 'email') {
          setEmailError(result.message || 'Revisá el email.');
        }
        toast.error(result.message || 'No se pudo enviar la consulta.');
        return;
      }
      toast.success('Tu consulta fue enviada con éxito. Te contactaremos pronto.');
      resetForm();
    } catch (err) {
      console.error('Collection inquiry: fallo al enviar', err);
      toast.error('No se pudo enviar la consulta. Intentá de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const subjectLabel = parcelLabel || 'este lote';

  if (notEnabled) {
    return (
      <Box
        id={formId}
        data-testid="collection-inquiry-not-enabled"
        sx={{
          p: 1.5,
          borderRadius: '8px',
          border: `1px solid ${colors.border}`,
          bgcolor: colors.backgroundDark,
        }}
      >
        <Typography sx={{ fontSize: '12px', color: colors.textSecondary, fontFamily: typography.fontFamily.primary }}>
          {LOT_INQUIRY_NOT_ENABLED_MESSAGE}
        </Typography>
        <Button
          size="small"
          onClick={() => setNotEnabled(false)}
          sx={{
            mt: 0.5,
            px: 0,
            minWidth: 0,
            textTransform: 'none',
            fontSize: '12px',
            color: colors.textSecondary,
            fontFamily: typography.fontFamily.primary,
          }}
        >
          Volver a intentar
        </Button>
      </Box>
    );
  }

  return (
    <Box
      id={formId}
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        p: 1.5,
        borderRadius: '8px',
        border: `1px solid ${colors.border}`,
        bgcolor: colors.backgroundDark,
      }}
    >
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 600,
          color: colors.textPanelTitle,
          fontFamily: typography.fontFamily.heading,
        }}
      >
        Enviar consulta
      </Typography>
      {manageMode ? (
        <InfoNotice
          data-testid="collection-inquiry-manage-notice"
          title="Estás cargando una consulta para un tercero."
          description="Usá los datos de contacto del cliente."
        />
      ) : (
        <Typography sx={{ fontSize: '12px', color: colors.textSecondary, fontFamily: typography.fontFamily.primary }}>
          Dejanos tus datos para que puedan responderte sobre {subjectLabel}.
          {!isLoggedIn ? ' Iniciá sesión para enviar la consulta.' : ''}
        </Typography>
      )}
      <TextField
        size="small"
        label="Nombre"
        value={name}
        onChange={(event) => setName(event.target.value.slice(0, MAX_NAME))}
        disabled={submitting || !isLoggedIn}
        inputProps={{ maxLength: MAX_NAME }}
        sx={showroomBrandedFieldSx(accentColor)}
      />
      <TextField
        size="small"
        type="email"
        label="Email"
        value={email}
        onChange={(event) => {
          setEmail(event.target.value);
          if (emailError) setEmailError('');
        }}
        disabled={submitting || !isLoggedIn || emailLocked}
        autoComplete={manageMode ? 'off' : 'email'}
        inputProps={{ inputMode: 'email', readOnly: emailLocked, 'data-testid': 'collection-inquiry-email' }}
        error={Boolean(emailError)}
        helperText={emailError || undefined}
        sx={showroomBrandedFieldSx(accentColor)}
      />
      <TextField
        size="small"
        label="Teléfono"
        value={phone}
        onChange={(event) => setPhone(sanitizePhoneInput(event.target.value))}
        disabled={submitting || !isLoggedIn}
        autoComplete="tel"
        inputProps={{
          maxLength: MAX_PHONE,
          inputMode: 'tel',
        }}
        sx={showroomBrandedFieldSx(accentColor)}
      />
      <TextField
        size="small"
        label="Mensaje"
        multiline
        minRows={2}
        maxRows={6}
        value={message}
        onChange={(event) => setMessage(event.target.value.slice(0, MAX_MESSAGE))}
        disabled={submitting || !isLoggedIn}
        inputProps={{ maxLength: MAX_MESSAGE }}
        placeholder={`Quiero consultar por ${subjectLabel}`}
        sx={showroomBrandedFieldSx(accentColor)}
      />
      <Button
        type="submit"
        variant="contained"
        sx={accentColor ? showroomBrandedPrimaryButtonSx(accentColor) : FORM_PRIMARY_BUTTON_SX}
        disabled={submitting}
      >
        {submitting ? 'Enviando…' : (isLoggedIn ? 'Enviar consulta' : 'Iniciar sesión para consultar')}
      </Button>
    </Box>
  );
}
